/**
 * Hook para gerenciamento de Deals/Oportunidades CRM
 * Fornece queries e mutations para CRUD de negócios
 * Versão: 1.1.0
 * RELEASE: 2026-09-03 v1.1.0 - Bloqueios do Workflow nas transições protegidas
 *          passam a usar o aviso modal global, sem toast vermelho de erro.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';

// Logger para o módulo de Deals
const logger = createLogger('CrmDeals');

// Tipos de status e prioridade
export type DealStatus = 'open' | 'won' | 'lost';
export type DealPriority = 'low' | 'medium' | 'high' | 'urgent';

// Interface do Deal
export interface CrmDeal {
  id: string;
  tenant_id: string;
  pipeline_id: string;
  phase_id: string;
  contact_id: string | null;
  seller_id?: string | null;
  title: string;
  company_name: string | null;
  description: string | null;
  description_html?: string | null;  // v2.2.0: Texto rico formatado
  value: number;
  currency: string;
  probability: number | null;
  expected_close_date: string | null;
  actual_close_date: string | null;
  status: DealStatus;
  priority: DealPriority;
  loss_reason: string | null;
  loss_reason_id?: string | null; // v2.6.0: FK crm_loss_reasons (motivo estruturado)
  assigned_to: string | null;
  source: string | null;
  sort_order: number;
  custom_fields: Record<string, unknown>;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // v2.5.0 (2026-05-16): Origem do cadastro do negócio (manual/import/webhook/etc)
  creation_origin?: {
    type: 'manual' | 'import' | 'webhook' | 'scheduling' | 'flow' | 'api';
    source_id?: string | null;
    source_name?: string | null;
    extra?: Record<string, unknown> | null;
  } | null;
  // Relacionamentos carregados
  contact?: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    avatar_url: string | null;
    // v1.0.1: Campos de redes sociais do contato
    channel_ids?: {
      instagram_username?: string;
      instagram_igsid?: string;
      facebook_psid?: string;
      webchat_visitor_id?: string;
    } | null;
    metadata?: Record<string, unknown> | null;
    // Cliente vinculado ao contato (via crm_customers.main_contact_id)
    customer?: {
      id: string;
      nome: string | null;
      nome_fantasia: string | null;
      razao_social: string | null;
      customer_type: 'pf' | 'pj';
      cpf: string | null;
      cnpj: string | null;
    }[] | null;
  } | null;
  phase?: {
    id: string;
    name: string;
    color: string | null;
    is_won: boolean;
    is_lost: boolean;
    sort_order: number; // Posição da fase no pipeline
  } | null;
  pipeline?: {
    id: string;
    name: string;
    workspace_id?: string | null;
    // v2.4.1: Nome e cor do workspace (já retornados pela RPC get_deal_by_id)
    // Usados na seção Informações do modal premium para distinguir negócios entre múltiplos workspaces
    workspace_name?: string | null;
    workspace_color?: string | null;
  } | null;
  assigned_user?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  // Vendedor (crm_sellers)
  seller?: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    avatar_url: string | null;
    // Profile vinculado ao usuário do vendedor (fonte de verdade do avatar/nome real)
    profile?: { full_name: string | null; avatar_url: string | null } | null;
  } | null;

  // Criador do deal (profiles)
  created_by_user?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  // v1.9.0: Último contrato do deal (retornado pela RPC get_kanban_column)
  last_contract_status?: string | null;
  last_contract_title?: string | null;
  // v2.0.0: customer_id para abrir modal de cliente pelo Kanban
  customer_id?: string | null;
  // v2.3.0: Data de atualização da fase para ordenação dinâmica no Kanban
  phase_updated_at?: string | null;
  // v2.5.0: Agregação inline do badge de cadência per_phase na fase atual
  // (vinda de get_kanban_column v2.2.0). Evita N+1 de list_deal_cadence_steps.
  cadence_phase_total?: number;
  cadence_phase_pending?: number;
  cadence_phase_has_overdue?: boolean;
  // v2.4.0: Bloco de Indicação / Afiliado vinculado ao deal (NULL quando não houver)
  // Populado pela RPC get_deal_by_id v2.4.0 — usado na sidebar do modal premium
  referral?: {
    id: string;
    program_name: string | null;
    campaign_name: string | null;
    affiliate_id: string | null;
    affiliate_name: string | null;
    affiliate_avatar: string | null;
    lead_source: string | null;
    lead_message: string | null;
  } | null;
}

// Input para criar deal
export interface CreateDealInput {
  pipeline_id: string;
  phase_id: string;
  title: string;
  company_name?: string;
  description?: string;
  contact_id?: string | null;
  value?: number;
  currency?: string;
  expected_close_date?: string | null;
  priority?: DealPriority;
  assigned_to?: string | null;
  source?: string;
  seller_id?: string | null;  // Vendedor (crm_sellers)
  probability?: number | null;  // Probabilidade customizada (0-100)
  custom_fields?: Record<string, unknown>;
}

// Input para atualizar deal
export interface UpdateDealInput {
  id: string;
  pipeline_id?: string;
  phase_id?: string;
  title?: string;
  company_name?: string | null;
  description?: string | null;
  contact_id?: string | null;
  value?: number;
  currency?: string;
  expected_close_date?: string | null;
  priority?: DealPriority;
  assigned_to?: string | null;
  source?: string | null;
  seller_id?: string | null;  // Vendedor (crm_sellers)
  probability?: number | null;  // Probabilidade customizada (0-100)
  status?: DealStatus;
  loss_reason?: string | null;
  custom_fields?: Record<string, unknown>;
}

// Filtros para listagem (expandido para Kanban v1.1.0)
export interface DealFilters {
  pipeline_id?: string | null;
  phase_id?: string | null;
  workspace_id?: string | null;  // Filtro por workspace (v1.3.0)
  status?: DealStatus | null;
  assigned_to?: string | null;
  priority?: DealPriority | null;
  search?: string;
  // Filtros avançados para Kanban
  seller_id?: string | null;
  probability_min?: number;
  probability_max?: number;
  source?: string | null;
  // v3.4.0 (2026-07-11): Multi-seleção de origens (source_ids)
  source_ids?: string[] | null;
  product_id?: string | null;
  category_id?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  // v3.0.0: Novos filtros de data
  created_from?: string | null;
  created_to?: string | null;
  won_date_from?: string | null;
  won_date_to?: string | null;
  lost_date_from?: string | null;
  lost_date_to?: string | null;
  phase_updated_from?: string | null;
  phase_updated_to?: string | null;
  // v3.1.0 (2026-04-23): Tags e Data Previsão
  tag_ids?: string[] | null;
  /** Recorte especial usado pelo detalhamento para negócios ainda sem tags. */
  tags_is_empty?: boolean;
  expected_close_from?: string | null;
  expected_close_to?: string | null;
  // v3.2.0 (2026-05-16): Origem do Cadastro (manual/import/webhook/scheduling/flow/api)
  creation_origin_types?: string[] | null;
  creation_origin_source_id?: string | null;
  // v3.3.0 (2026-07-11): Motivos de perda (multi-seleção) — só afeta negócios perdidos
  loss_reason_ids?: string[] | null;
  // v3.5.0 (2026-09-07): Grupos "sem valor" da listagem agrupada
  // (get_deals_paginated v1.13.0). Quando true, retorna apenas registros
  // sem vendedor / sem workspace / sem origem.
  seller_is_null?: boolean;
  workspace_is_null?: boolean;
  source_is_null?: boolean;
  // v3.6.0 (2026-09-17): recortes de auditoria por produto/serviço e cliente
  // (get_deals_paginated v1.14.0), usados pelo drill-down dos indicadores.
  product_ids?: string[] | null;
  item_unit_types?: string[] | null;
  recurrence_intervals?: string[] | null;
  term_types?: string[] | null;
  exclude_unit_types?: string[] | null;
  customer_ids?: string[] | null;
  // v3.7.0 (2026-10-05): somente negócios com item que recebeu desconto
  has_discount?: boolean | null;
}


// Opções para controle da query de deals
interface UseCrmDealsOptions {
  /** Se false, desabilita a query automática (útil quando só precisa das mutations) */
  enabled?: boolean;
}

/**
 * Hook principal para gerenciamento de Deals CRM
 * @param filters - Filtros para listagem de deals
 * @param options - Opções do hook (enabled: false para desabilitar query)
 */
export function useCrmDeals(filters?: DealFilters, options?: UseCrmDealsOptions) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();
  const workflowNotice = useWorkflowGuardNotice();

  // Controle de habilitação da query (permite usar apenas mutations)
  const queryEnabled = options?.enabled !== false;

  // Query para listar deals com relacionamentos
  // NOTA: No Kanban, essa query é DESABILITADA - usamos RPC get_kanban_column por coluna
  // Query para listar deals com relacionamentos
  // NOTA: No Kanban, essa query é DESABILITADA - usamos RPC get_kanban_column por coluna
  const dealsQuery = useQuery({
    queryKey: ['crm-deals', currentTenant?.id, filters],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      // ==========================================
      // PRÉ-FILTRO: Buscar pipeline_ids do workspace (se filtro ativo)
      // PostgREST não suporta .eq() em campos de tabelas relacionadas
      // ==========================================
      let workspacePipelineIds: string[] | null = null;
      
      if (filters?.workspace_id) {
        const { data: workspacePipelines } = await supabase
          .from('crm_pipelines')
          .select('id')
          .eq('workspace_id', filters.workspace_id)
          .eq('tenant_id', currentTenant.id);
        
        workspacePipelineIds = workspacePipelines?.map(p => p.id) || [];
        
        // Se o workspace não tem pipelines, retornar vazio imediatamente
        if (workspacePipelineIds.length === 0) {
          logger.info('Workspace sem pipelines, retornando lista vazia');
          return [];
        }
      }

      let query = supabase
        .from('crm_deals')
        .select(`
          *,
          contact:contacts(id, name, phone, email, avatar_url),
          phase:crm_pipeline_phases(id, name, color, is_won, is_lost, sort_order),
          pipeline:crm_pipelines(id, name, workspace_id),
          assigned_user:profiles!crm_deals_assigned_to_fkey(id, full_name, avatar_url),
          seller:crm_sellers(id, name, email, phone, avatar_url, profile:profiles!crm_sellers_user_id_fkey(full_name, avatar_url)),
          created_by_user:profiles!crm_deals_created_by_fkey(id, full_name, avatar_url)
        `)
        .eq('tenant_id', currentTenant.id)
        .order('sort_order', { ascending: true });

      // Filtro de workspace via pipeline_ids (v1.3.1 - corrigido)
      if (workspacePipelineIds !== null) {
        query = query.in('pipeline_id', workspacePipelineIds);
      }

      // Aplicar filtros
      if (filters?.pipeline_id) {
        query = query.eq('pipeline_id', filters.pipeline_id);
      }
      if (filters?.phase_id) {
        query = query.eq('phase_id', filters.phase_id);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.assigned_to) {
        query = query.eq('assigned_to', filters.assigned_to);
      }
      if (filters?.priority) {
        query = query.eq('priority', filters.priority);
      }
      if (filters?.search) {
        query = query.or(`title.ilike.%${filters.search}%,company_name.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Erro ao buscar deals:', error.message);
        throw error;
      }
      return data as unknown as CrmDeal[];
    },
    // Query só executa se tenant existe E queryEnabled for true
    enabled: !!currentTenant?.id && queryEnabled,
  });

  // Mutation para criar deal via RPC atômica
  const createDeal = useMutation({
    mutationFn: async (input: CreateDealInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      // Chama RPC atômica com validações completas (v1.2.0 - inclui probability)
      const { data: dealId, error } = await supabase.rpc('create_crm_deal', {
        p_title: input.title,
        p_pipeline_id: input.pipeline_id,
        p_phase_id: input.phase_id,
        p_contact_id: input.contact_id || null,
        p_company_name: input.company_name || null,
        p_description: input.description || null,
        p_value: input.value || 0,
        p_currency: input.currency || 'BRL',
        p_priority: input.priority || 'medium',
        p_expected_close_date: input.expected_close_date || null,
        p_assigned_to: input.assigned_to || null,
        p_source: input.source || null,
        p_probability: input.probability ?? null,  // Probabilidade customizada (v1.2.0)
        p_seller_id: input.seller_id || null,  // Vendedor (v1.1.0)
        p_custom_fields: input.custom_fields ? JSON.parse(JSON.stringify(input.custom_fields)) : {},
      });

      if (error) {
        logger.error('Erro ao criar deal via RPC:', error.message);
        throw error;
      }
      
      // Retorna objeto com IDs para invalidação correta do cache
      return { 
        id: dealId as string, 
        pipeline_id: input.pipeline_id, 
        phase_id: input.phase_id 
      };
    },
    onSuccess: (result) => {
      // Invalida cache do CRUD tradicional
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      // Invalida listagem paginada (CrmDeals.tsx) para exibir novo negócio instantaneamente
      queryClient.invalidateQueries({ queryKey: ['crm-deals-paginated'] });
      
      // Invalida cache do Kanban (coluna específica)
      queryClient.invalidateQueries({ 
        queryKey: ['kanban-column', result.pipeline_id, result.phase_id] 
      });
      // Invalida stats do pipeline
      queryClient.invalidateQueries({ 
        queryKey: ['pipeline-phase-stats', result.pipeline_id] 
      });
      
      toast.success('Negócio criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar negócio: ${error.message}`);
    },
  });

  // Mutation para atualizar deal via RPC atômica
  const updateDeal = useMutation({
    mutationFn: async (input: UpdateDealInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      // Chama RPC atômica com validações completas (v1.1.0 - inclui seller_id)
      // Chama RPC atômica com validações completas (v1.2.0 - inclui probability)
      const { data: dealId, error } = await supabase.rpc('update_crm_deal', {
        p_deal_id: input.id,
        p_title: input.title || null,
        p_pipeline_id: input.pipeline_id || null,
        p_phase_id: input.phase_id || null,
        p_contact_id: input.contact_id ?? null,
        p_company_name: input.company_name ?? null,
        p_description: input.description ?? null,
        p_value: input.value ?? null,
        p_currency: input.currency || null,
        p_priority: input.priority || null,
        p_expected_close_date: input.expected_close_date || null,
        p_assigned_to: input.assigned_to ?? null,
        p_source: input.source ?? null,
        p_probability: input.probability ?? null,  // Probabilidade customizada (v1.2.0)
        p_seller_id: input.seller_id ?? null,  // Vendedor (v1.1.0)
        p_custom_fields: input.custom_fields ? JSON.parse(JSON.stringify(input.custom_fields)) : null,
      });

      if (error) {
        logger.error('Erro ao atualizar deal via RPC:', error.message);
        throw error;
      }
      
      // Retorna objeto com IDs para invalidação correta do cache
      return { 
        id: dealId as string, 
        pipeline_id: input.pipeline_id, 
        phase_id: input.phase_id 
      };
    },
    onSuccess: (result) => {
      // Invalida cache do CRUD tradicional
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['crm-deal', result.id] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      // Invalida listagem paginada para exibir alterações instantaneamente
      queryClient.invalidateQueries({ queryKey: ['crm-deals-paginated'] });
      
      // Invalida cache do Kanban se pipeline/fase alterados
      if (result.pipeline_id && result.phase_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['kanban-column', result.pipeline_id, result.phase_id] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['pipeline-phase-stats', result.pipeline_id] 
        });
      }
      
      toast.success('Negócio atualizado com sucesso!');
    },
    onError: (error: Error) => {
      // Bloqueio de fase/status emitido pelo Workflow vira aviso de processo.
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      toast.error(`Erro ao atualizar negócio: ${error.message}`);
    },
  });

  // Mutation para deletar deal
  const deleteDeal = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_deals')
        .delete()
        .eq('id', id);

      if (error) {
        logger.error('Erro ao deletar deal:', error.message);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      toast.success('Negócio excluído com sucesso!');
    },
    onError: (error: Error) => {
      // Detecta erro de foreign key para dar mensagem mais intuitiva
      if (error.message.includes('foreign key constraint')) {
        if (error.message.includes('crm_customers')) {
          toast.error(
            'Não foi possível excluir este negócio',
            { description: 'Este negócio gerou um cliente vinculado. Verifique os dados do cliente antes de tentar novamente.' }
          );
        } else {
          toast.error(
            'Não foi possível excluir este negócio',
            { description: 'Existem registros vinculados que impedem a exclusão.' }
          );
        }
      } else {
        toast.error(`Erro ao excluir negócio: ${error.message}`);
      }
    },
  });

  // Mutation para mover deal entre fases (drag-and-drop)
  const moveDealToPhase = useMutation({
    mutationFn: async ({ dealId, newPhaseId, sortOrder }: { 
      dealId: string; 
      newPhaseId: string; 
      sortOrder?: number;
    }) => {
      const { data, error } = await supabase
        .rpc('move_deal_to_phase', {
          p_deal_id: dealId,
          p_new_phase_id: newPhaseId,
          p_new_sort_order: sortOrder ?? null,
        });

      if (error) {
        logger.error('Erro ao mover deal:', error.message);
        throw error;
      }
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      // Invalida colunas do Kanban e stats das fases para refletir movimentação
      queryClient.invalidateQueries({ queryKey: ['kanban-column'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-phase-stats'] });
      // v1.1.0: Invalida cache individual do deal para atualizar sidebar do modal (Pipeline/Fase)
      queryClient.invalidateQueries({ queryKey: ['crm-deal', variables.dealId] });
      // v1.2.0: Invalida dados CRM do Kanban de Conversas
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });
      // v1.3.0 (2026-07-11) - Cross-workspace: força refetch de cadência/playbook/
      // timeline quando o move pode ter cancelado a cadência antiga e/ou iniciado
      // uma nova via triggers (trg_a_log_deal_workspace_move + trg_z_start_playbook_on_move).
      // Sem isto, a modal aberta permanece com dados stale da workspace anterior.
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', variables.dealId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', variables.dealId] });
      queryClient.invalidateQueries({ queryKey: ['deal-playbook-eligibility', variables.dealId] });
      queryClient.invalidateQueries({ queryKey: ['deal-modal-data', variables.dealId] });
      queryClient.invalidateQueries({ queryKey: ['deal-activities', variables.dealId] });
    },

    onError: (error: Error) => {
      // Mapeia ERRCODE='check_violation' (guarda won↔lost na RPC v2.4.0+)
      // numa mensagem amigável, em vez do prefixo genérico.
      const msg = error.message || '';
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      if (
        msg.includes('Reabra o negócio antes') ||
        msg.includes('não pode ser movido para fase de perda') ||
        msg.includes('não pode ser movido para fase de ganho')
      ) {
        toast.error(msg);
        return;
      }
      toast.error(`Erro ao mover negócio: ${msg}`);
    },
  });

  // Mutation para marcar como ganho - v2.0.0: Valida produtos/serviços obrigatórios
  const markAsWon = useMutation({
    mutationFn: async (dealId: string) => {
      // v2.0.0: Verifica se deal possui pelo menos 1 produto/serviço
      const { count: itemsCount, error: countError } = await supabase
        .from('crm_deal_items')
        .select('id', { count: 'exact', head: true })
        .eq('deal_id', dealId);
      
      if (countError) {
        logger.error('Erro ao verificar items do deal:', countError.message);
        throw countError;
      }
      
      if (!itemsCount || itemsCount === 0) {
        throw new Error('É necessário adicionar ao menos um produto/serviço antes de marcar como ganho');
      }

      // Buscar deal para obter pipeline_id e phase_id atual
      const { data: currentDeal, error: dealError } = await supabase
        .from('crm_deals')
        .select('id, pipeline_id, phase_id')
        .eq('id', dealId)
        .single();

      if (dealError) throw dealError;
      if (!currentDeal?.pipeline_id) throw new Error('Deal sem pipeline associado');

      // Buscar fase de ganho (is_won=true) do pipeline
      const { data: wonPhase, error: phaseError } = await supabase
        .from('crm_pipeline_phases')
        .select('id')
        .eq('pipeline_id', currentDeal.pipeline_id)
        .eq('is_won', true)
        .maybeSingle();

      if (phaseError) throw phaseError;

      // Tipo seguro do Supabase para update de deals (marcar como ganho)
      const updateData: Database['public']['Tables']['crm_deals']['Update'] = {
        status: 'won',
        actual_close_date: new Date().toISOString().split('T')[0],
      };

      // Se existe fase de ganho, move o deal para ela
      if (wonPhase?.id) {
        updateData.phase_id = wonPhase.id;
      }

      const { data, error } = await supabase
        .from('crm_deals')
        .update(updateData)
        .eq('id', dealId)
        .select()
        .single();

      if (error) throw error;
      
      // Retorna deal atualizado com phase_id anterior para invalidação
      return { ...data, previous_phase_id: currentDeal.phase_id };
    },
    onSuccess: (data) => {
      // Invalida lista de deals
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      // Invalida cache individual do deal para atualizar modal
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: ['crm-deal', data.id] });
      }
      // v1.2.0: Invalida dados CRM do Kanban de Conversas
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });
      // v1.7.0: Invalida cache Kanban - coluna origem E destino
      if (data?.pipeline_id) {
        // Invalida coluna de origem (anterior)
        if (data?.previous_phase_id) {
          queryClient.invalidateQueries({ 
            queryKey: ['kanban-column', data.pipeline_id, data.previous_phase_id] 
          });
        }
        // Invalida coluna de destino (nova fase)
        if (data?.phase_id) {
          queryClient.invalidateQueries({ 
            queryKey: ['kanban-column', data.pipeline_id, data.phase_id]
          });
        }
        queryClient.invalidateQueries({ 
          queryKey: ['pipeline-phase-stats', data.pipeline_id] 
        });
      }
      toast.success('🎉 Negócio marcado como ganho!');
    },
    onError: (error: Error) => {
      // v2.1.0: Detecta erro do trigger validate_deal_status_transition (NO_WON_PHASE)
      // e exibe a mensagem amigável vinda do banco diretamente, sem prefixo genérico.
      const msg = error.message || '';
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      if (msg.includes('Não é possível marcar como Ganho')) {
        toast.error(msg);
        return;
      }
      toast.error(`Erro ao marcar como ganho: ${msg}`);
    },
  });

  // Mutation para marcar como perdido - v1.8.0: aceita targetPhaseId opcional
  // Quando houver múltiplas fases is_lost no pipeline (motivos de perda),
  // o caller passa targetPhaseId já escolhido pelo usuário no modal de seleção.
  const markAsLost = useMutation({
    mutationFn: async ({ dealId, lossReason, targetPhaseId }: { dealId: string; lossReason?: string; targetPhaseId?: string }) => {
      // Buscar deal para obter pipeline_id e phase_id atual
      const { data: currentDeal, error: dealError } = await supabase
        .from('crm_deals')
        .select('id, pipeline_id, phase_id')
        .eq('id', dealId)
        .single();

      if (dealError) throw dealError;
      if (!currentDeal?.pipeline_id) throw new Error('Deal sem pipeline associado');

      // Resolver fase de destino:
      // 1) Se caller já passou targetPhaseId → usa diretamente (cenário multi-motivos)
      // 2) Senão busca fase única is_lost do pipeline (compat retro)
      let resolvedPhaseId: string | null = targetPhaseId ?? null;

      if (!resolvedPhaseId) {
        const { data: lostPhases, error: phaseError } = await supabase
          .from('crm_pipeline_phases')
          .select('id')
          .eq('pipeline_id', currentDeal.pipeline_id)
          .eq('is_lost', true);

        if (phaseError) throw phaseError;

        // Se houver múltiplas, exige escolha explícita (UI deve abrir modal antes)
        if ((lostPhases?.length ?? 0) > 1) {
          throw new Error('Pipeline possui múltiplas fases de perda. Selecione qual usar.');
        }
        resolvedPhaseId = lostPhases?.[0]?.id ?? null;
      }

      // Tipo seguro do Supabase para update de deals (marcar como perdido)
      const updateData: Database['public']['Tables']['crm_deals']['Update'] = {
        status: 'lost',
        loss_reason: lossReason || null,
        actual_close_date: new Date().toISOString().split('T')[0],
      };

      // Se existe fase de perda resolvida, move o deal para ela
      if (resolvedPhaseId) {
        updateData.phase_id = resolvedPhaseId;
      }

      const { data, error } = await supabase
        .from('crm_deals')
        .update(updateData)
        .eq('id', dealId)
        .select()
        .single();

      if (error) throw error;
      
      // Retorna deal atualizado com phase_id anterior para invalidação
      return { ...data, previous_phase_id: currentDeal.phase_id };
    },
    onSuccess: (data) => {
      // Invalida lista de deals
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      // Invalida cache individual do deal para atualizar modal
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: ['crm-deal', data.id] });
      }
      // v1.2.0: Invalida dados CRM do Kanban de Conversas
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });
      // v1.7.0: Invalida cache Kanban - coluna origem E destino
      if (data?.pipeline_id) {
        // Invalida coluna de origem (anterior)
        if (data?.previous_phase_id) {
          queryClient.invalidateQueries({ 
            queryKey: ['kanban-column', data.pipeline_id, data.previous_phase_id] 
          });
        }
        // Invalida coluna de destino (nova fase)
        if (data?.phase_id) {
          queryClient.invalidateQueries({ 
            queryKey: ['kanban-column', data.pipeline_id, data.phase_id] 
          });
        }
        queryClient.invalidateQueries({ 
          queryKey: ['pipeline-phase-stats', data.pipeline_id] 
        });
      }
      toast.info('Negócio marcado como perdido');
    },
    onError: (error: Error) => {
      // v2.1.0: Detecta erro do trigger validate_deal_status_transition (NO_LOST_PHASE)
      // e exibe a mensagem amigável vinda do banco diretamente, sem prefixo genérico.
      const msg = error.message || '';
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      if (msg.includes('Não é possível marcar como Perdido')) {
        toast.error(msg);
        return;
      }
      toast.error(`Erro ao marcar como perdido: ${msg}`);
    },
  });

  // Mutation para reabrir deal
  const reopenDeal = useMutation({
    mutationFn: async (dealId: string) => {
      const { data, error } = await supabase
        .from('crm_deals')
        .update({
          status: 'open',
          actual_close_date: null,
          loss_reason: null,
        })
        .eq('id', dealId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Invalida lista de deals
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline-stats'] });
      // v1.6.0: Invalida cache individual do deal para atualizar modal
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: ['crm-deal', data.id] });
      }
      // v1.2.0: Invalida dados CRM do Kanban de Conversas
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-crm-data'] });
      // v1.6.1: Invalida cache Kanban para atualizar cards em tempo real
      if (data?.pipeline_id) {
        if (data?.phase_id) {
          queryClient.invalidateQueries({ 
            queryKey: ['kanban-column', data.pipeline_id, data.phase_id] 
          });
        }
        queryClient.invalidateQueries({ 
          queryKey: ['pipeline-phase-stats', data.pipeline_id] 
        });
      }
      toast.success('Negócio reaberto!');
    },
    onError: (error: Error) => {
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      toast.error(`Erro ao reabrir negócio: ${error.message}`);
    },
  });

  return {
    deals: dealsQuery.data || [],
    isLoading: dealsQuery.isLoading,
    error: dealsQuery.error,
    createDeal,
    updateDeal,
    deleteDeal,
    moveDealToPhase,
    markAsWon,
    markAsLost,
    reopenDeal,
    refetch: dealsQuery.refetch,
  };
}

/**
 * Interface do retorno da RPC get_deal_by_id
 * Estrutura flat retornada pelo banco
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-12
 */
interface DealByIdRpcResult {
  id: string;
  title: string;
  company_name: string | null;
  description: string | null;
  description_html: string | null;  // v2.2.0: Texto rico formatado
  value: number;
  currency: string;
  priority: string;
  status: string;
  probability: number | null;
  expected_close_date: string | null;
  actual_close_date: string | null;
  source: string | null;
  custom_fields: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  tenant_id: string;
  // IDs de referência
  pipeline_id: string;
  phase_id: string;
  contact_id: string | null;
  customer_id: string | null;
  assigned_to: string | null;
  seller_id: string | null;
  created_by: string | null;
  // Contato
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  contact_avatar: string | null;
  // v1.0.1: Campos de redes sociais do contato
  contact_channel_ids: {
    instagram_username?: string;
    instagram_igsid?: string;
    facebook_psid?: string;
    webchat_visitor_id?: string;
  } | null;
  contact_metadata: Record<string, unknown> | null;
  // Customer
  customer_nome: string | null;
  customer_nome_fantasia: string | null;
  customer_razao_social: string | null;
  customer_type: string | null;
  customer_cpf: string | null;
  customer_cnpj: string | null;
  customer_lifecycle_stage: string | null;
  // Fase
  phase_name: string | null;
  phase_color: string | null;
  phase_is_won: boolean;
  phase_is_lost: boolean;
  phase_sort_order: number;
  phase_probability_percent: number | null;
  // Pipeline
  pipeline_name: string | null;
  pipeline_workspace_id: string | null;  // v2.2.0: Nome correto do campo da RPC
  workspace_name: string | null;
  workspace_color: string | null;
  // Responsável
  assigned_user_name: string | null;
  assigned_user_email: string | null;
  assigned_user_avatar: string | null;
  // Vendedor (avatar já com fallback aplicado na RPC)
  seller_name: string | null;
  seller_email: string | null;
  seller_phone: string | null;
  seller_avatar: string | null;
  // Criador
  created_by_name: string | null;
  created_by_avatar: string | null;
  // v2.4.0: Indicação / Afiliado vinculado ao deal (NULL quando não houver)
  referral_id: string | null;
  referral_program_name: string | null;
  referral_campaign_name: string | null;
  referral_affiliate_id: string | null;
  referral_affiliate_name: string | null;
  referral_affiliate_avatar: string | null;
  referral_lead_source: string | null;
  referral_lead_message: string | null;
  // v2.5.0 (2026-05-16): Origem do cadastro
  creation_origin: {
    type: 'manual' | 'import' | 'webhook' | 'scheduling' | 'flow' | 'api';
    source_id?: string | null;
    source_name?: string | null;
    extra?: Record<string, unknown> | null;
  } | null;
  // v2.6.0: Motivo da perda (id + nota livre)
  loss_reason_id: string | null;
  loss_reason: string | null;
}

/**
 * Transforma resultado flat da RPC em estrutura aninhada CrmDeal
 * Mantém compatibilidade com componentes existentes
 * 
 * @param row - Resultado da RPC get_deal_by_id
 * @returns CrmDeal com estrutura aninhada
 */
function transformDealRpcResult(row: DealByIdRpcResult): CrmDeal {
  return {
    id: row.id,
    tenant_id: row.tenant_id,
    pipeline_id: row.pipeline_id,
    phase_id: row.phase_id,
    contact_id: row.contact_id,
    seller_id: row.seller_id,
    title: row.title,
    company_name: row.company_name,
    description: row.description,
    description_html: row.description_html,  // v2.2.0: Texto rico formatado
    value: row.value,
    currency: row.currency,
    probability: row.probability,
    expected_close_date: row.expected_close_date,
    actual_close_date: row.actual_close_date,
    status: row.status as DealStatus,
    priority: row.priority as DealPriority,
    loss_reason: row.loss_reason ?? null,
    loss_reason_id: row.loss_reason_id ?? null,
    assigned_to: row.assigned_to,
    source: row.source,
    sort_order: 0,
    custom_fields: row.custom_fields || {},
    metadata: {},
    created_by: row.created_by,
    created_at: row.created_at,
    updated_at: row.updated_at,
    // v2.5.0: Origem do cadastro
    creation_origin: row.creation_origin ?? null,
    // Estruturas aninhadas para compatibilidade
    contact: row.contact_id ? {
      id: row.contact_id,
      name: row.contact_name,
      phone: row.contact_phone,
      email: row.contact_email,
      avatar_url: row.contact_avatar,
      // v1.0.1: Campos de redes sociais
      channel_ids: row.contact_channel_ids,
      metadata: row.contact_metadata,
      // Customer vinculado ao contato
      customer: row.customer_nome || row.customer_razao_social ? [{
        id: row.customer_id || '',
        nome: row.customer_nome,
        nome_fantasia: row.customer_nome_fantasia,
        razao_social: row.customer_razao_social,
        customer_type: (row.customer_type || 'pf') as 'pf' | 'pj',
        cpf: row.customer_cpf,
        cnpj: row.customer_cnpj,
      }] : null,
    } : null,
    phase: row.phase_name ? {
      id: row.phase_id,
      name: row.phase_name,
      color: row.phase_color,
      is_won: row.phase_is_won,
      is_lost: row.phase_is_lost,
      sort_order: row.phase_sort_order,
    } : null,
    pipeline: row.pipeline_name ? {
      id: row.pipeline_id,
      name: row.pipeline_name,
      workspace_id: row.pipeline_workspace_id,  // v2.2.0: Mapeamento correto do campo
      // v2.4.1: Workspace exibido na seção Informações do modal premium
      workspace_name: row.workspace_name,
      workspace_color: row.workspace_color,
    } : null,
    assigned_user: row.assigned_to ? {
      id: row.assigned_to,
      full_name: row.assigned_user_name,
      avatar_url: row.assigned_user_avatar,
    } : null,
    // Vendedor com avatar já com fallback (vem da RPC)
    seller: row.seller_id ? {
      id: row.seller_id,
      name: row.seller_name || '',
      email: row.seller_email,
      phone: row.seller_phone,
      avatar_url: row.seller_avatar, // Já com COALESCE aplicado na RPC
    } : null,
    created_by_user: row.created_by ? {
      id: row.created_by,
      full_name: row.created_by_name,
      avatar_url: row.created_by_avatar,
    } : null,
    // v2.4.0: Bloco de Indicação / Afiliado (somente quando há referral vinculado)
    referral: row.referral_id ? {
      id: row.referral_id,
      program_name: row.referral_program_name,
      campaign_name: row.referral_campaign_name,
      affiliate_id: row.referral_affiliate_id,
      affiliate_name: row.referral_affiliate_name,
      affiliate_avatar: row.referral_affiliate_avatar,
      lead_source: row.referral_lead_source,
      lead_message: row.referral_lead_message,
    } : null,
  };
}

/**
 * Hook para buscar um deal específico por ID via RPC
 * Usa RPC get_deal_by_id que implementa fallback de avatar do vendedor
 * 
 * @version 2.0.0
 * RELEASE: 2026-01-12 - Migrado para RPC com fallback de avatar
 */
export function useCrmDeal(dealId: string | null) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['crm-deal', dealId],
    queryFn: async () => {
      if (!dealId || !currentTenant?.id) return null;

      logger.debug('Buscando deal via RPC:', { dealId });

      // Chamada RPC com validação de tenant
      const { data, error } = await (supabase.rpc as any)('get_deal_by_id', {
        p_deal_id: dealId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar deal via RPC:', error.message);
        throw error;
      }

      // RPC retorna array, pegar primeiro (e único) resultado
      if (!data || data.length === 0) {
        logger.warn('Deal não encontrado:', dealId);
        return null;
      }

      // Transformar resultado flat em estrutura aninhada
      return transformDealRpcResult(data[0] as DealByIdRpcResult);
    },
    enabled: !!dealId && !!currentTenant?.id,
  });
}

/**
 * Hook para estatísticas do pipeline
 */
export function useCrmPipelineStats(pipelineId: string | null) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['crm-pipeline-stats', pipelineId],
    queryFn: async () => {
      if (!pipelineId) return [];

      const { data, error } = await supabase
        .rpc('get_pipeline_stats', { p_pipeline_id: pipelineId });

      if (error) {
        logger.error('Erro ao buscar stats do pipeline:', error.message);
        throw error;
      }
      return data as {
        phase_id: string;
        phase_name: string;
        phase_color: string | null;
        phase_sort_order: number;
        deal_count: number;
        total_value: number;
        is_won: boolean;
        is_lost: boolean;
      }[];
    },
    enabled: !!pipelineId && !!currentTenant?.id,
  });
}
